import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { array, number, object, oneOfType, string } from 'prop-types'
import { APIProvider, Map, Pin, useMap } from '@vis.gl/react-google-maps'
import { useTranslation } from 'react-i18next'
import { getPhotoMapPosition } from '@util/functions'
import Div from '@ui/layout/Div'
import Icon from '@ui/elements/Icon'
import Modal from '@ui/component/Modal'
import PhotoZoomViewer from '@ui/component/PhotoZoomViewer'
import PhotoMarker from './PhotoMarker'

const FitMarkersBounds = ({ positions }) => {
  const map = useMap()

  useEffect(() => {
    if (!map || !positions.length) return

    if (1 === positions.length) {
      map.setCenter(positions[0])
      map.setZoom(19)
      return
    }

    const bounds = new window.google.maps.LatLngBounds()
    positions.forEach(position => bounds.extend(position))
    map.fitBounds(bounds, 50)
  }, [map, positions.map(position => `${position.lat},${position.lng}`).join('|')])

  return null
}

const PhotoLocation = ({ position, address }) => {
  const [t] = useTranslation('sterilization')

  return (
    <Div cssClass='photo-location'>
      {address && <p><span>{t('labels.photo_address')} : </span>{address}</p>}
      <p><span>{t('labels.photo_gps')} : </span>{`${position.lat}, ${position.lng}`}</p>
    </Div>
  )
}

const CallPhotosMap = ({ callId, photos, defaultCenter, address }) => {
  const [activePhotoId, setActivePhotoId] = useState(null)
  const [zoomedPhoto, setZoomedPhoto] = useState(null)
  const locatedPhotos = photos
    .map(photo => ({ ...photo, mapPosition: getPhotoMapPosition(photo.position) }))
    .filter(photo => photo.mapPosition)

  if (!locatedPhotos.length) {
    return <></>
  }

  return (
    <Div cssClass='call-photos-map'>
      <APIProvider apiKey={import.meta.env.VITE_MAPS_KEY} region='fr' language='fr_FR'>
        <Map
          style={{ width: '100%', height: '500px' }}
          defaultZoom={19}
          defaultCenter={defaultCenter ?? locatedPhotos[0].mapPosition}
          fullscreenControl={true}
          gestureHandling="cooperative"
          mapId={`sterilization-call-photos-${callId}`}
        >
          <FitMarkersBounds positions={locatedPhotos.map(photo => photo.mapPosition)} />
          {locatedPhotos.map(photo => <PhotoMarker
            key={`call-${callId}-photo-marker-${photo.id}`}
            position={photo.mapPosition}
            pin={<Pin background={photo.isSecondScan ? '#07adf5' : '#F9A33D'} borderColor='#444444' glyphColor='#f1efef' />}
            isOpen={activePhotoId === photo.id}
            onClick={() => setActivePhotoId(id => id === photo.id ? null : photo.id)}
            onClose={() => setActivePhotoId(null)}
          >
            <Div cssClass='map-photo-preview'>
              <img src={photo.scanPhoto} alt='' onClick={() => setZoomedPhoto(photo)} />
              <Icon icon='icon-zoom_in' spanClass='is-medium' callback={() => setZoomedPhoto(photo)} />
            </Div>
          </PhotoMarker>)}
        </Map>
        {createPortal(
          <Modal cssClass='call-photo-zoom-modal' isActive={null !== zoomedPhoto} closeCallback={() => setZoomedPhoto(null)} closeAction={true}>
            {zoomedPhoto && <>
              <PhotoZoomViewer src={zoomedPhoto.scanPhoto} />
              <PhotoLocation position={zoomedPhoto.position} address={address} />
            </>}
          </Modal>,
          document.body
        )}
      </APIProvider>
    </Div>
  )
}

PhotoLocation.propTypes = {
  position: object,
  address: string
}

FitMarkersBounds.propTypes = {
  positions: array
}

CallPhotosMap.propTypes = {
  callId: oneOfType([number, string]),
  photos: array,
  defaultCenter: object,
  address: string
}

export default CallPhotosMap
